<template>
    <section class="event-frame">
        <section class="event">
            <template v-if="step == 0">
                <app-alert v-if="errorMessage" type="error">{{ errorMessage }}</app-alert>
            </template>

            <template v-if="step == 1">
                <div class="event-details">
                    <h1 class="visuallyhidden">Event Registration</h1>
                    <h2>{{ event.title }}</h2>
                    <info-bar :dual-line="true">
                        <div class="info-bar-column">
                            <span class="title">Location</span>
                            <span>
                                <template v-if="event.location">{{ event.location.name }}</template>
                                <template v-else>No Location</template>
                            </span>
                        </div>
                        <div class="info-bar-column">
                            <span class="title">Time</span>
                            <span>
                                {{ [ event.start_time, "HH:mm:ss" ] | moment("h:mma") }}
                                - {{ [ event.end_time, "HH:mm:ss" ] | moment("h:mma") }}
                            </span>
                        </div>
                        <div class="info-bar-column">
                            <span class="title">Date</span>
                            <span>{{ date }}</span>
                        </div>
                        <div v-if="event.capacity" class="info-bar-column">
                            <span class="title">Capacity</span>
                            <span>{{ event.attending_count }} / {{ event.capacity }}</span>
                        </div>
                    </info-bar>
                    <div class="description" v-html="compiledDescription"/>
                </div>

                <div class="event-form">
                    <app-form ref="infoForm" 
                              :model="student" 
                              :rules="rules" 
                              @submit.native.prevent="submit">
                        <grid :columns="['1fr', '1fr']">
                            <grid-item width="2">
                                <app-alert v-if="errorMessage" type="error">{{ errorMessage }}</app-alert>
                                <app-alert>
                                    To sign up for the event, please fill in the fields below. Any field marked with * is required.

                                    <template v-if="event.restricted_by">
                                        <br><br>This event does have registration restrictions. Please read the description for more information.
                                    </template>
                                </app-alert>
                            </grid-item>
                            <grid-item>
                                <app-form-item label="First Name" prop="first_name">
                                    <app-input id="first_name" v-model="student.first_name" autofocus/>
                                </app-form-item>
                            </grid-item>
                            <grid-item>
                                <app-form-item label="Last Name" prop="last_name">
                                    <app-input id="last_name" v-model="student.last_name"/>
                                </app-form-item>
                            </grid-item>
                            <grid-item width="2">
                                <app-form-item label="Email" prop="email">
                                    <app-input id="email" v-model="student.email"/>
                                </app-form-item>
                            </grid-item>
                            <grid-item width="2">
                                <app-form-item label="School Identifier" prop="school_identifier">
                                    <app-input id="school_identifier" v-model="student.school_identifier"/>
                                </app-form-item>
                            </grid-item>
                        </grid>
                        <app-button
                            type="primary"
                            native-type="submit"
                            size="large">Sign Up</app-button>
                    </app-form>
                </div>
            </template>

            <div v-if="step == 2">
                <p>{{ successMessage }}</p>
            </div>
        </section>
    </section>
</template>

<script>
import marked from 'marked'
import moment from 'moment'
import { first } from '@/utilities'
import axios from '@/network/axios'

import InfoBar from '../../ui/InfoBar'
export default {
    name: 'event-signup',
    components: {
        InfoBar
    },
    props: ['uuid'],
    head: {
        title: {
            inner: 'Event'
        }
    },
    data() {
        return {
            step: 0,
            loading: false,
            errorMessage: null,
            successMessage: null,
            student: {
                first_name: '',
                last_name: '',
                email: '',
                school_identifier: ''
            },
            event: null,
            rules: {
                first_name: [
                    {
                        required: true,
                        message: 'Please enter a first name',
                        trigger: 'blur'
                    }
                ],
                last_name: [
                    {
                        required: true,
                        message: 'Please enter a last name',
                        trigger: 'blur'
                    }
                ],
                email: [
                    {
                        required: true,
                        message: 'Please enter an email',
                        trigger: 'blur'
                    }
                ]
            }
        }
    },
    computed: {
        compiledDescription() {
            return marked(this.event.description)
        },
        date() {
            return moment.utc(this.event.date).format("MM/DD/YYYY")
        }
    },
    mounted() {
        this.getEvent()
    },
    methods: {
        getEvent() {
            axios.get('/api/v2/event/' + this.uuid).then(({ data }) => {
                this.event = data.data
                this.step = 1
            },
            ({ response }) => {
                this.errorMessage = first(response.data.messages)
            })
        },
        submit() {
            this.loading = true
            axios.post('/api/v2/event/' + this.uuid, this.student).then(({ data }) => {
                this.loading = false
                this.step = 2
                this.successMessage = data.data.message
            },
            ({ response }) => {
                this.loading = false
                this.errorMessage = first(response.data.messages)
            })
        },
        goToLogin() {
            this.$router.push({
                name: 'login',
                params: { email: this.user.email }
            })
        }
    }
}
</script>

<style lang="stylus">
@import '../../../stylus/variables'

.event
    max-width: 960px
    width: 100%
    display: flex
    justify-content: center
    padding-top: 40px
    margin: 0px auto

    @media screen and (max-width: 800px)
        display: block
        padding: 20px

.event-details
    max-width: 520px
    width: 100%
    padding: 0px 20px 20px
    color: $text-medium-grey

    .info-bar
        margin: 0px
        padding: 10px 0px
        border-bottom: 1px solid $border-color

    h2
        display: block
        margin: 0px 0px
        padding: 10px 0px
        font-size: 30px
        border-bottom: 1px solid $border-color

    .description
        h1, h2, h3, h4, h5
            border-bottom: 0px
            margin: 0px
            padding: 5px 0px

        h1
            font-size: 36px

        h2
            font-size: 24px

        h3, h4, h5
            font-size: 18px

    @media screen and (max-width: 800px)
        width: 100%
        border-radius: 20px
        box-shadow: 0px 5px 20px rgba(0,0,0,.15)
        padding: 0px 20px 20px
        margin-left: 20px

        .auth-error
            background: #FFF
            border-radius: 3px
            font-size: 15px
            color: #FC4545
            padding: 10px 0px

        .el-steps
            margin: 25px 0px 20px

        .app-form
            padding-top: 10px
        .app-form .app-form-item
            margin-bottom: 18px
        
        p
            margin-top: 0px

.event-form
    background: $white
    max-width: 420px
    width: 100%
    border-radius: 20px
    box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.15)
    padding: 0px 20px 20px
    margin-left: 20px

    .auth-error
        background: #FFF
        border-radius: 3px
        font-size: 15px
        color: #FC4545
        padding: 10px 0px

    .el-steps
        margin: 25px 0px 20px

    .el-form
        padding-top: 10px

    .el-form .el-form-item
        margin-bottom: 18px

    p
        font-size: 16px

        &.instructions
            font-size: 14px
            color: $medium-text-color

    @media screen and (max-width: 800px)
        width: 100%
        max-width: 800px
        margin: 0px
</style>
